import { useEffect, useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  fetchNotifications,
  fetchMoreNotifications,
  fetchUnreadCount,
  readNotification,
  readAllNotifications,
  removeNotification,
  setUnreadCount,
  setRealTimeNotifications,
  setSystemNotifications,
  setAdditionalNotifications,
  setRealTimeConnected,
  resetNotifications
} from '../store/slices/notificationSlice';
import {
  subscribeToUnreadCount,
  subscribeToAllNotifications
} from '../services/firebase/notification.service';
import { useAuth } from './useAuth';
import { Capacitor } from '@capacitor/core';
import { Badge } from '@capawesome/capacitor-badge';

export const useNotifications = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  
  const { 
    notifications, 
    systemNotifications,
    additionalNotifications,
    unreadCount, 
    unreadSysCount,
    unreadAddCount,
    isLoading, 
    error, 
    hasMore, 
    oldestTimestamp,
    isRealTimeConnected 
  } = useAppSelector((state) => state.notifications);
  
  // Merge all notifications and sort by date
  const allNotifications = useMemo(() => {
    const merged = [
      ...notifications,
      ...systemNotifications,
      ...additionalNotifications
    ];
    return merged.sort((a, b) => {
      const aTime = a.dateCreated || ('timestamp' in a ? a.timestamp : 0) || 0;
      const bTime = b.dateCreated || ('timestamp' in b ? b.timestamp : 0) || 0;
      return bTime - aTime;
    });
  }, [notifications, systemNotifications, additionalNotifications]);
  
  // Calculate total unread count
  const totalUnreadCount = useMemo(() => {
    return unreadCount + unreadSysCount + unreadAddCount;
  }, [unreadCount, unreadSysCount, unreadAddCount]);

  // Update app icon badge when unread count changes
  useEffect(() => {
    const updateBadge = async () => {
      // Only update badge on native platforms
      if (Capacitor.getPlatform() === 'web') return;

      try {
        // Check if we have permission to show badges
        const { isSupported } = await Badge.isSupported();
        if (!isSupported) return;

        if (totalUnreadCount > 0) {
          await Badge.set({ count: totalUnreadCount });
        } else {
          await Badge.clear();
        }
      } catch (error) {
        // Badge API may not be available on all devices
        console.warn('Failed to update badge:', error);
      }
    };

    updateBadge();
  }, [totalUnreadCount]);

  // Subscribe to real-time notifications when component mounts
  useEffect(() => {
    let unsubscribeCount: (() => void) | null = null;
    let unsubscribeNotifications: (() => void) | null = null;
    
    if (user?.uid) {
      // Subscribe to unread count
      unsubscribeCount = subscribeToUnreadCount(user.uid, (count) => {
        dispatch(setUnreadCount(count));
      });
      
      // Subscribe to all notification streams
      unsubscribeNotifications = subscribeToAllNotifications(user.uid, {
        onNotifications: (notifications) => {
          dispatch(setRealTimeNotifications(notifications));
        },
        onSystemNotifications: (notifications) => {
          dispatch(setSystemNotifications(notifications));
        },
        onAdditionalNotifications: (notifications) => {
          dispatch(setAdditionalNotifications(notifications));
        }
      });
      
      // Mark as connected
      dispatch(setRealTimeConnected(true));
      
      // Fetch initial unread count
      dispatch(fetchUnreadCount(user.uid));
    }
    
    return () => {
      if (unsubscribeCount) {
        unsubscribeCount();
      }
      if (unsubscribeNotifications) {
        unsubscribeNotifications();
      }
      dispatch(setRealTimeConnected(false));
    };
  }, [user?.uid, dispatch]);

  // Get user notifications
  const loadNotifications = useCallback((limit?: number) => {
    if (user?.uid) {
      dispatch(fetchNotifications({ userId: user.uid, limit }));
    }
  }, [user?.uid, dispatch]);

  // Load more notifications for pagination
  const loadMoreNotifications = useCallback((limit?: number) => {
    if (user?.uid && oldestTimestamp && hasMore) {
      dispatch(fetchMoreNotifications({ 
        userId: user.uid, 
        limit, 
        startAfterTimestamp: oldestTimestamp 
      }));
    }
  }, [user?.uid, oldestTimestamp, hasMore, dispatch]);

  // Mark a notification as read
  const markAsRead = useCallback((notificationId: string) => {
    if (user?.uid) {
      // Find the notification to determine its type
      let notificationType: 'regular' | 'system' | 'additional' | undefined;
      
      if (systemNotifications.find(n => n.id === notificationId)) {
        notificationType = 'system';
      } else if (additionalNotifications.find(n => n.id === notificationId)) {
        notificationType = 'additional';
      } else {
        notificationType = 'regular';
      }
      
      dispatch(readNotification({ userId: user.uid, notificationId, notificationType }));
    }
  }, [user?.uid, dispatch, systemNotifications, additionalNotifications]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    if (user?.uid) {
      dispatch(readAllNotifications(user.uid));
    }
  }, [user?.uid, dispatch]);

  // Delete a notification
  const deleteNotification = useCallback((notificationId: string) => {
    if (user?.uid) {
      // Find the notification to determine its type
      let notificationType: 'regular' | 'system' | 'additional' | undefined;
      
      if (systemNotifications.find(n => n.id === notificationId)) {
        notificationType = 'system';
      } else if (additionalNotifications.find(n => n.id === notificationId)) {
        notificationType = 'additional';
      } else {
        notificationType = 'regular';
      }
      
      dispatch(removeNotification({ userId: user.uid, notificationId, notificationType }));
    }
  }, [user?.uid, dispatch, systemNotifications, additionalNotifications]);

  // Reset notifications state (useful when logging out)
  const resetNotificationsState = useCallback(() => {
    dispatch(resetNotifications());
  }, [dispatch]);

  return {
    notifications: isRealTimeConnected ? allNotifications : notifications,
    systemNotifications,
    additionalNotifications,
    allNotifications,
    unreadCount: totalUnreadCount,
    unreadRegularCount: unreadCount,
    unreadSysCount,
    unreadAddCount,
    isLoading,
    error,
    hasMore,
    isRealTimeConnected,
    loadNotifications,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    resetNotificationsState,
  };
};

export default useNotifications;